import { makeAutoObservable, observable, runInAction } from "mobx";
import PortalService from "../../../../../services/api/user-service";
import {rootContainer, TYPE} from "../../../../../services/container/root";
import IframeService from "../../../../../services/iframe-service/iframe-service";
import MerchantPortalMenuItemStore from "./merchant-portal-menu-item-store";

export type MerchantPortalId = string|number;

export type MerchantPortalDto = {
    id: MerchantPortalId;
    label: string;
    portals?: Array<MerchantPortalDto>;
}

class MerchantPortalMenuListStore {
    private _isLoading = false;
    private _portals: MerchantPortalDto[] = [];
    private _openGroups = observable.map<string|number, boolean>();
    private _portalMenuItems: MerchantPortalMenuItemStore[] = [];
    private _searchValue: string | null = null;
    private _selectedPortal: MerchantPortalMenuItemStore | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _portalService(): PortalService {
        return rootContainer.get<PortalService>(TYPE.UserService);
    }
    private get _iframeService(): IframeService {
        return rootContainer.get<IframeService>(TYPE.IframService);
    }

    public get portals(): MerchantPortalDto[] {
        return this._portals;
    }

    public get searchValue(): string {
        return this._searchValue || '';
    }

    public get lowercaseSearchValue(): string {
        return this.searchValue.toLowerCase();
    }

    public get filteredPortals() {
        if (!this._searchValue) {
            return this._portalMenuItems;
        }

        return this._findMatchingPortals(this.lowercaseSearchValue);
    }

    public get portalsCount(): number {
        return this.portals.reduce((acc, portal) => {
            if (portal.portals && portal.portals.length > 0) {
                return acc + portal.portals.length;
            }
            return acc + 1;
        }, 0)
    }

    public get selectedPortal(): MerchantPortalMenuItemStore | null {
        return this._selectedPortal;
    }

    public setSearchValue(v: string) {
        this._searchValue = v;
    }

    public findAndSetSelectedPortalById(portalId: MerchantPortalId) {
        if (this._portalMenuItems.length === 0) {
            return;
        }

        const portal = this._findMatchingPortalById(portalId, this._portalMenuItems);

        if (portal) {
            this._selectedPortal = portal;
        }
    }

    private _findMatchingPortals(searchValue: string): MerchantPortalMenuItemStore[] {
        return this._portalMenuItems.filter(portal => {
            const doesLabelMatch = portal?.lowercaseLabel.includes(searchValue);
            return doesLabelMatch || (portal.portals.some((portal: MerchantPortalMenuItemStore) => portal.lowercaseLabel.includes(searchValue)))
        });
    }

    private _findMatchingPortalById(portalId: MerchantPortalId, portals: MerchantPortalMenuItemStore[]): MerchantPortalMenuItemStore|null {
        for (const portal of portals) {
            if (portal.portals.length > 0) {
                const matchingPortal = this._findMatchingPortalById(portalId, portal.portals);

                if (matchingPortal) {
                    return matchingPortal;
                }
            }
            else {
                if (portal.id === portalId) {
                    return portal;
                }
            }
        }
        return null;
    }

    public async loadPortals() {
        this._isLoading = true;

        try {
            const portals = await this._portalService.getPortals();

            runInAction(() => {
                this._portals = portals;
                this._portalMenuItems = MerchantPortalMenuItemStore.fromMerchantPortals(portals);
            });
        }
        finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }
    public async changePortal(portalId: string|number, reason?: string) {
        try {
            this._isLoading = true;

            const res = await this._portalService.changePortal(portalId, reason);
            this._iframeService.redirectParentFrame(res.redirectTo);
        }
        finally {
            runInAction(() => {
                this._isLoading = false;
            })
        }
    }
}

export default new MerchantPortalMenuListStore();